import React, { useState, useEffect } from "react";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import DeleteModal from "../../components/DeleteBlogModal";
import { apiRequestHandler } from "../../apiConfig/service";
import { toast } from "react-toastify";
import { url } from "../../apiConfig/ApiConfig";
import { formatDate } from "../../utils/formatedDate";
import DataTable from "react-data-table-component";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

const getAllCategories = async () => {
  const res = await apiRequestHandler({
    endPoint: "getAdminCategory",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res?.data;
};

const validationSchema = Yup.object({
  title: Yup.string().required("Category title is required"),
});

const CategoryManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryFormMode, setCategoryFormMode] = useState("add");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);


  const navigate = useNavigate();

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const res = await getAllCategories();
      if (res) {
        const sorted = res.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setCategories(sorted);
      } else {
        toast.error("Failed to fetch categories");
      }
    } catch (err) {
      toast.error("Error while fetching categories");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const filtered =
      categories?.filter((cat) =>
        cat?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [];
    setFilteredCategories(filtered);
  }, [searchTerm, categories]);

  const handleCategoryDeleted = async (id) => {
    try {
      setIsLoading(true);
      const res = await apiRequestHandler({
        method: "DELETE",
        endPoint: `${url}/category/${id}`,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res?.success) {
        toast.success(res?.message);
        fetchCategories();
      } else {
        toast.error(res?.message);
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (mode, category = null) => {
    setCategoryFormMode(mode);
    setSelectedCategory(category);
    setShowCategoryModal(true);
  };

  const handleCloseModal = () => {
    setShowCategoryModal(false);
    setSelectedCategory(null);
  };

  const handleSubmitCategory = async (values, { resetForm }) => {
    const res = await apiRequestHandler({
      method: "POST",
      endPoint: "addCategory",
      data: {
        name: values.title,
        categoryId: categoryFormMode === "edit" ? selectedCategory?._id : undefined,
      },
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });

    if (res?.success) {
      toast.success(res.message);
      resetForm();
      handleCloseModal();
      fetchCategories();
    } else {
      toast.error(res.message);
    }
  };

  const columns = [
    {
      name: 'Sr No.',
      selector: (row, index) => (currentPage - 1) * 10 + index + 1,
      width: '80px',
    }
    ,
    {
      name: "Title",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Created At",
      selector: (row) => formatDate(row.createdAt),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <FaEye
            title="View"
            style={{ color: "#064a8c", marginRight: "10px", cursor: "pointer" }}
            onClick={() => handleOpenModal("view", row)}
          />
          <FaEdit
            title="Edit"
            style={{ color: "#ff9800", marginRight: "10px", cursor: "pointer" }}
            onClick={() => handleOpenModal("edit", row)}
          />
          <FaTrash
            title="Delete"
            style={{ color: "#f44336", cursor: "pointer" }}
            onClick={() => {
              setCategoryToDelete(row);
              setShowDeleteModal(true);
            }}
          />
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div style={{ minHeight: "100vh", padding: "20px", background: "#f5f7fa" }}>
      <div style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "10px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <h4 style={{ margin: 0 }}>Category Management</h4>
          <button
            onClick={() => handleOpenModal("add")}
            style={{
              background: "linear-gradient(to bottom right, #163252, #0081ff)",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            + Add Category
          </button>
        </div>

        <DeleteModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          theme="dark"
          onConfirm={() => {
            handleCategoryDeleted(categoryToDelete?._id);
            setShowDeleteModal(false);
          }}
        />
      </div>

      <div className="modifyTable" style={{ borderRadius: "10px", marginTop: "20px" }}>
        <DataTable
          title=""
          columns={columns}
          data={filteredCategories}
          pagination
          highlightOnHover
          onChangePage={setCurrentPage}
          paginationDefaultPage={currentPage}
          responsive
          striped
          progressPending={isLoading}
          subHeader
          subHeaderComponent={
            <input
              type="text"
              placeholder="Search by category title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "14px",
                width: "250px",
              }}
            />
          }
        />
      </div>

      {showCategoryModal && (
        <div className="modal show fade d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {categoryFormMode === "view"
                    ? "View Category"
                    : categoryFormMode === "edit"
                      ? "Edit Category"
                      : "Add Category"}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <Formik
                  enableReinitialize
                  initialValues={{
                    title: selectedCategory?.name || "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmitCategory}
                >
                  {({ isSubmitting }) => (
                    <Form>
                      <div className="mb-3">
                        <label className="form-label">Category Title</label>
                        <Field
                          name="title"
                          className="form-control"
                          readOnly={categoryFormMode === "view"}
                        />
                        <div className="text-danger small">
                          <ErrorMessage name="title" />
                        </div>
                      </div>
                      {categoryFormMode !== "view" && (
                        <div className="text-center">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="submitBtn w-100"
                          >
                            {categoryFormMode === "edit" ? "Update Category" : "Submit Category"}
                          </button>
                        </div>
                      )}
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;